import {isEqual, isUndefined} from 'lodash';
import memoize from 'lru-memoize';
import {ReactNode} from 'react';
import {Key} from 'ts-key-enum';

import {collectEnumValues, makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {ConversationSortPropertiesEnum} from '../../../src/models/api/conversationCommonTypes';
import {SelectedFilters} from '../../../src/models/api/localPreferencesModel';
import {RecipientRolesEnum} from '../../../src/models/api/recipientModel';
import {CellsSortDirectionsEnum} from '../../../src/models/api/sortOptionModel';
import {
  InboxesFiltersEnum,
  InboxesSearchLocationsEnum,
  RInboxesSearchLocation,
  TeammatesMessageFoldersEnum,
} from '../../../src/models/url/inboxesUrlModel';
import {
  InboxesConversationsParameters,
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
  InboxesUserParameters,
} from '../../../src/store/panels/inboxes/parameters/inboxesParameters';
import {
  emptyInboxesSelection,
  messageFolderToConversationFolder,
} from '../../../src/store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ModifiersEnum} from './searchTypes';

export type SuggestionSelectHandler = (
  text: string,
  selectKey?: Key,
  options?: {
    insertWhiteSpace?: boolean;
    replaceWholeQuery?: boolean;
    shouldSaveOriginalQuery?: boolean;
    isCurrentQuery?: boolean;
    isRecentQuery?: boolean;
    isQuickFilter?: boolean;
    isSmartMixItem?: boolean;
    isModifierSuggestion?: boolean;
    modifierSuggestionType?: ModifiersEnum;
    isConversationSuggestion?: boolean;
    modifierHelperType?: ModifiersEnum;
  },
) => void;

export interface SearchToken {
  modifier?: ModifiersEnum;
  value?: string;
}

export interface SearchQueryPart {
  text: string;
  startOffset: number;
  endOffset: number;
  token?: SearchToken;
}

export interface SearchItemProps {
  value: string;
  primaryLabel: ReactNode;
  secondaryLabel?: ReactNode;
}

export interface SearchSuggestionsProps {
  activeToken: SearchToken;
  query?: string;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Const.
 */

export const maxSearchSuggestions = 5;
export const selectedSearchFilterKey = 'search';

export const DEFAULT_SEARCH_FILTERS = [
  InboxesSearchLocationsEnum.LOCAL,
  InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
  InboxesSearchLocationsEnum.GLOBAL,
] as const;

export const MOBILE_SEARCH_FILTERS = [
  InboxesSearchLocationsEnum.LOCAL,
  InboxesSearchLocationsEnum.GLOBAL,
] as const;

export const modifiers = new Set(collectEnumValues(ModifiersEnum));

export const validModifierValues = new Map(
  Object.entries({
    [ModifiersEnum.IS]: [
      'unread',
      'unreplied',
      'archived',
      'closed',
      'open',
      'deleted',
      'trashed',
      'spam',
      'following',
      'subscribed',
      'muted',
      'tracking',
      'shared-with-me',
      'discussion',
      'unassigned',
      'snoozed',
      'assigned',
      'starred',
      'in-my-workspace',
    ],
    [ModifiersEnum.HAS]: [
      'attachments',
      'attachment',
      'followers',
      'follower',
      'subscribers',
      'subscriber',
      'no-tags',
      'no-tag',
      'reminder',
      'calendar',
      'draft',
    ],
    [ModifiersEnum.IN]: ['messages', 'comments', 'attachments', 'all'],
    [ModifiersEnum.TYPE]: [
      'custom',
      'facebook',
      'email',
      'googleplay',
      'intercom',
      'internal',
      'layer',
      'smooch',
      'phone-call',
      'call',
      'sms',
      'tweet',
      'tweet_dm',
      'whatsapp',
      'yalo_wha',
      'front_chat',
      'discussion',
    ],
  }),
);

/**
 * "                                # opening double-quote…
 * [^"]*                            # …followed by zero or more chars that are not a double-quote…
 * "?                               # …maybe closing double-quote
 */
const doubleQuotesRegExp = '"[^"]*"?';

/**
 * (?:${[...modifiers].join('|')})  # non-capturing group including one modifier
 * (?::\\s*)?                       # maybe non-capturing group including a colon followed by zero or more spaces
 * (                                # maybe non capturing group including:
 *  [^\\s"]+                        #   - anything that's not a space or a double-quote
 * |                                # or
 *  ${doubleQuotesRegExp}           #   - opening double-quote followed by zero or more chars that are not a double-quote and maybe closing double-quote
 * )?
 */
const modifierRegExp = `(?:${[...modifiers].join('|')})(?::\\s*)?(?:[^\\s"]+|${doubleQuotesRegExp})?`;

/**
 * /S+                              # anything that is not a whitespace
 */
const anythingButWhitespaceRegExp = '\\S+';

const searchQuerySplitRegExp = new RegExp(
  `(${modifierRegExp}|${doubleQuotesRegExp}|${anythingButWhitespaceRegExp})`,
);

export const fuzzySortBaseOptions = {
  allowTypo: true,
  limit: maxSearchSuggestions,
  threshold: -20,
};

const emptySearchQueryPart: SearchQueryPart = {
  text: '',
  startOffset: 0,
  endOffset: 0,
  token: {
    value: '',
  },
};

/*
 * Helpers.
 */

export function splitSearchQuery(query: string): SearchQueryPart[] {
  let currentOffset = 0;
  const parts = query
    .split(searchQuerySplitRegExp)
    .filter((text) => text.length > 0)
    .map((text) => {
      const startOffset = currentOffset;
      const endOffset = currentOffset + text.length;
      currentOffset = endOffset;

      return {
        text,
        startOffset,
        endOffset,
        token: buildSearchToken(text),
      };
    });

  return parts.length > 0 ? parts : [emptySearchQueryPart];
}

export function findActiveToken(query: string, offset = 0): SearchToken | undefined {
  const searchQueryParts = splitSearchQuery(query);

  const activePartIndex = searchQueryParts.findIndex((part) =>
    Boolean(part.startOffset <= offset && offset <= part.endOffset),
  );

  // `token` does not exist in a search query part only if we're matching on space(s).
  // If this is the case, try to fall back to the next search query part, or the previous one.
  return (
    searchQueryParts[activePartIndex]?.token ||
    searchQueryParts[activePartIndex + 1]?.token ||
    searchQueryParts[activePartIndex - 1]?.token
  );
}

export const findMemoizedActiveToken = memoize()(findActiveToken);

export function buildSearchToken(query: string): SearchToken | undefined {
  if (query.trim().length === 0) {
    return undefined;
  }

  if (!query.includes(':')) {
    return {value: query};
  }

  // Isolate potential modifier and value.
  const [modifier, value] = query.split(':');
  const lowerCasedModifier = modifier.toLowerCase();

  if (isExistingModifier(lowerCasedModifier)) {
    return {
      modifier: lowerCasedModifier,
      value: value.trim(),
    };
  }

  return {value: query};
}

export function insertSuggestionInPlace(
  query: string,
  newTokenText: string,
  offset = query.length,
  shouldInsertBlank = false,
): string {
  const activeToken = findActiveToken(query, offset);

  return splitSearchQuery(query)
    .map(({text, token}, partIndex, allParts) => {
      if (token && isEqual(token, activeToken)) {
        const maybeBlank = shouldInsertBlank && partIndex === allParts.length - 1 ? ' ' : '';
        return newTokenText + maybeBlank;
      }

      return text;
    })
    .join('');
}

export function isWildcardToken(token: SearchToken, query?: string) {
  return isUndefined(query) || !query.length || (isUndefined(token.modifier) && token.value === '+');
}

export function findSelectedInboxesSearchLocation(selectedFilters: SelectedFilters) {
  const selectedSearchFilter = selectedFilters[selectedSearchFilterKey];
  if (!selectedSearchFilter || !RInboxesSearchLocation.is(selectedSearchFilter)) {
    return InboxesSearchLocationsEnum.LOCAL;
  }

  return selectedSearchFilter;
}

export function getOtherInboxesSearchFilter(currentFilter: InboxesSearchLocationsEnum) {
  if (currentFilter === InboxesSearchLocationsEnum.LOCAL) {
    return InboxesSearchLocationsEnum.GLOBAL;
  }

  return InboxesSearchLocationsEnum.LOCAL;
}

export function isValidModifierValue(token: SearchToken): boolean {
  if (
    token.modifier &&
    validModifierValues.has(token.modifier) &&
    !(validModifierValues.get(token.modifier) || []).includes(token.value?.trim() || '')
  ) {
    return false;
  }

  return true;
}

/*
 * Type guards.
 */

export const isExistingModifier = makeEnumTypeGuard(ModifiersEnum);

/*
 * Search attributes.
 */

export const searchableAttribute = 'data-searchable';

export const searchableAttrsSubject = {
  [searchableAttribute]: true,
  [`${searchableAttribute}-${ModifiersEnum.SUBJECT}`]: true,
};

export const searchableAttrsBody = {
  [searchableAttribute]: true,
  [`${searchableAttribute}-${ModifiersEnum.BODY}`]: true,
};

export const searchableAttrsFrom = makeSearchableAttrsForRole(RecipientRolesEnum.FROM);

export function makeSearchableAttrsForRole(role: RecipientRolesEnum) {
  const searchableAttrs: Record<string, boolean> = {
    [searchableAttribute]: true,
    [`${searchableAttribute}-${ModifiersEnum.RECIPIENT}`]: true,
  };

  // If role has a related modifier, add it as a searchable attribute.
  if (isExistingModifier(role)) {
    searchableAttrs[`${searchableAttribute}-${role}`] = true;
  }

  return searchableAttrs;
}

export function updateParametersWithSearch(
  parameters: InboxesConversationsParameters,
  search: InboxesSearchParameters | undefined,
): InboxesConversationsParameters {
  // Search => Normal.
  if (!search) {
    return {
      ...parameters,
      selection: parameters.search?.fromSelection || emptyInboxesSelection,
      activity: undefined,
      search: undefined,
    };
  }

  // Normal => Search & Search => Search.
  return {
    ...parameters,
    selection: emptyInboxesSelection,
    activity: undefined,
    search: {
      ...search,
      fromSelection: search.fromSelection || parameters.selection,
    },
  };
}

export function createBaseUserParameters(
  userId: number,
  messageFolder: TeammatesMessageFoldersEnum,
  sortProperty: ConversationSortPropertiesEnum = ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE,
  sortDirection: CellsSortDirectionsEnum = CellsSortDirectionsEnum.DESCENDING,
): InboxesUserParameters {
  return {
    type: InboxesParametersTypesEnum.USER,
    teammateId: userId,
    folder: messageFolderToConversationFolder(messageFolder),
    filters: [InboxesFiltersEnum.OPEN],
    sortDirection,
    sortProperty,
    selection: emptyInboxesSelection,
  };
}

export function parseAccountFilter(searchQuery: string): string | undefined {
  const queryParts = splitSearchQuery(searchQuery);

  // Find the first query part for `account`, if it exists.
  const firstAccountPart = queryParts.find(
    (part) => part.token && part.token.modifier === ModifiersEnum.ACCOUNT,
  );

  if (!firstAccountPart) {
    return undefined;
  }

  return firstAccountPart.token?.value;
}
